import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  getProfile,
  getWeeklyReport,
  getMonthlyReport,
  getCombinedReport,
  generateIndividualFeedback,
  generateCoupleFeedback
} from '../services/api';
import './Reports.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Reports() {
  const navigate = useNavigate();
  const loggedInUsername = localStorage.getItem('username');

  const [profile, setProfile] = useState(null);
  const [reportType, setReportType] = useState('weekly');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [feedback, setFeedback] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');

  useEffect(() => {
    if (!loggedInUsername) { navigate('/login'); return; }
    fetchProfile();
  }, [loggedInUsername, navigate]);

  const fetchProfile = async () => {
    try {
      setLoadingProfile(true);
      const profileData = await getProfile(loggedInUsername);
      if (profileData.exists !== false &&
          profileData.maleName &&
          profileData.femaleName &&
          profileData.maleAge &&
          profileData.femaleAge) {
        setProfile(profileData);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleGenerateReport = async () => {
    setError('');
    setReportData(null);
    setFeedback('');

    if (!selectedPerson) {
      setError('Please select whose report to generate');
      return;
    }

    setLoading(true);

    try {
      let data;
      if (selectedPerson === 'couple') {
        data = await getCombinedReport(loggedInUsername, profile.maleName, profile.femaleName, reportType);
      } else {
        if (reportType === 'weekly') {
          data = await getWeeklyReport(loggedInUsername, selectedPerson);
        } else if (reportType === 'monthly') {
          data = await getMonthlyReport(loggedInUsername, selectedPerson);
        }
      }
      setReportData(data);
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.error === 'REPORT_LIMIT_REACHED') {
        setError('⭐ ' + errorData.message);
      } else if (errorData?.error === 'PREMIUM_REQUIRED') {
        setError('⭐ ' + errorData.message);
      } else {
        setError('Failed to generate report. Please try again.');
      }
      console.error('Error generating report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFeedback = async () => {
    setFeedbackError('');
    setFeedbackLoading(true);

    try {
      let response;
      if (selectedPerson === 'couple') {
        response = await generateCoupleFeedback(loggedInUsername, profile.maleName, profile.femaleName, reportType);
      } else {
        response = await generateIndividualFeedback(loggedInUsername, selectedPerson, reportType);
      }

      if (response.success) {
        setFeedback(response.feedback);
      } else {
        setFeedbackError('Failed to generate feedback. Please try again.');
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.error === 'PREMIUM_REQUIRED') {
        setFeedbackError('⭐ AI feedback is a Premium feature. Upgrade to Premium to unlock.');
      } else {
        setFeedbackError('Failed to generate feedback. Please try again.');
      }
      console.error('Error generating feedback:', err);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const getPersonOptions = () => {
    if (loadingProfile) return [<option key="loading" value="">Loading...</option>];
    if (!profile || !profile.maleName || !profile.femaleName || !profile.maleAge || !profile.femaleAge) {
      return [<option key="none" value="">Complete your profile first</option>];
    }
    return [
      <option key="select" value="">Select...</option>,
      <option key="male" value={profile.maleName}>{profile.maleName} (Male Partner)</option>,
      <option key="female" value={profile.femaleName}>{profile.femaleName} (Female Partner)</option>,
      <option key="couple" value="couple">Couple's Joint Report</option>
    ];
  };

  const isPrivateCategory = (category) => category === 'Private';

  const renderCategoryChart = (report) => {
    if (!report || !report.categoryAverages) return null;
    const categories = Object.keys(report.categoryAverages);
    const averages = Object.values(report.categoryAverages);

    const data = {
      labels: categories.map(c => isPrivateCategory(c) ? '🔒 Private' : c),
      datasets: [{
        label: 'Average Rating',
        data: averages.map((avg, i) => isPrivateCategory(categories[i]) ? 0 : avg),
        backgroundColor: categories.map((c, i) => {
          if (isPrivateCategory(c)) return 'rgba(158, 158, 158, 0.4)';
          const avg = averages[i];
          if (avg >= 8) return 'rgba(76, 175, 80, 0.6)';
          if (avg >= 5) return 'rgba(255, 217, 61, 0.6)';
          return 'rgba(255, 107, 107, 0.6)';
        }),
        borderColor: categories.map((c, i) => {
          if (isPrivateCategory(c)) return 'rgba(158, 158, 158, 1)';
          const avg = averages[i];
          if (avg >= 8) return 'rgba(76, 175, 80, 1)';
          if (avg >= 5) return 'rgba(255, 217, 61, 1)';
          return 'rgba(255, 107, 107, 1)';
        }),
        borderWidth: 2,
      }],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: true, text: 'Category Performance', font: { size: 16, weight: 'bold' } },
        tooltip: {
          callbacks: {
            label: (context) => {
              const originalCategory = categories[context.dataIndex];
              if (isPrivateCategory(originalCategory)) return 'Private';
              return `Average Rating: ${context.parsed.y}`;
            }
          }
        }
      },
      scales: {
        y: { beginAtZero: true, max: 10, ticks: { stepSize: 1 } },
        x: { ticks: { maxRotation: 45, minRotation: 45 } },
      },
    };

    return <div className="chart-container"><Bar data={data} options={options} /></div>;
  };

  const renderCategoryDistribution = (report) => {
    if (!report || !report.categoryBreakdown) return null;
    const categories = Object.keys(report.categoryBreakdown);
    const counts = Object.values(report.categoryBreakdown);
    const palette = [
      'rgba(134, 96, 190, 0.8)', 'rgba(255, 107, 157, 0.8)', 'rgba(255, 107, 107, 0.8)',
      'rgba(76, 175, 80, 0.8)', 'rgba(255, 217, 61, 0.8)', 'rgba(33, 150, 243, 0.8)',
      'rgba(255, 152, 0, 0.8)', 'rgba(156, 39, 176, 0.8)', 'rgba(0, 188, 212, 0.8)',
      'rgba(139, 195, 74, 0.8)',
    ];

    const data = {
      labels: categories.map(c => isPrivateCategory(c) ? '🔒 Private' : c),
      datasets: [{
        data: counts,
        backgroundColor: categories.map((c, i) => isPrivateCategory(c) ? 'rgba(158, 158, 158, 0.6)' : palette[i % palette.length]),
        borderWidth: 2,
        borderColor: '#fff',
      }],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right' },
        title: { display: true, text: 'Entry Distribution by Category', font: { size: 16, weight: 'bold' } },
        tooltip: {
          callbacks: {
            label: (context) => {
              const originalCategory = categories[context.dataIndex];
              if (isPrivateCategory(originalCategory)) return 'Private';
              return `${originalCategory}: ${context.parsed}`;
            }
          }
        }
      },
    };

    return <div className="chart-container"><Doughnut data={data} options={options} /></div>;
  };

  const renderAIFeedback = () => {
    if (!reportData) return null;
    return (
      <div className="ai-feedback-section">
        <div className="feedback-header">
          <h3><span className="feedback-icon">🤖</span> AI-Powered Insights & Feedback</h3>
          <button className="generate-feedback-btn" onClick={handleGenerateFeedback} disabled={feedbackLoading}>
            {feedbackLoading ? '⏳ Generating...' : '✨ Generate Feedback'}
          </button>
        </div>

        {feedbackLoading && (
          <div className="feedback-loading">
            <div className="spinner"></div>
            <p>Analyzing your relationship patterns...</p>
          </div>
        )}

        {feedbackError && !feedbackLoading && (
          <div className="feedback-error">
            ⚠️ {feedbackError}
            {feedbackError.includes('Premium') && (
              <button className="upgrade-prompt-btn" onClick={() => navigate('/plus')}>
                ⭐ Upgrade to Premium
              </button>
            )}
          </div>
        )}

        {feedback && !feedbackLoading && (
          <div className="feedback-content">{feedback}</div>
        )}

        {!feedback && !feedbackLoading && !feedbackError && (
          <div className="feedback-empty">
            Click "Generate Feedback" to receive AI-powered insights about your relationship patterns, strengths, and areas for growth.
          </div>
        )}
      </div>
    );
  };

  const renderIndividualReport = (report, title) => {
    if (!report) return null;
    const hasPrivateEntries = report.categoryAverages &&
      Object.keys(report.categoryAverages).some(c => isPrivateCategory(c));

    return (
      <div className="report-section">
        <h2 className="report-title">{title}</h2>
        {hasPrivateEntries && (
          <div className="private-entries-notice">
            🔒 This report contains private entries. Private checkpoints are shown as <strong>"Private"</strong> and their details are hidden.
          </div>
        )}

        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <h3>Total Entries</h3>
              <p className="card-value">{report.totalEntries}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon">⭐</div>
            <div className="card-content">
              <h3>Overall Average</h3>
              <p className="card-value">{hasPrivateEntries ? 'Private' : (report.overallAverage?.toFixed(1) || 'N/A')}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon">🏆</div>
            <div className="card-content">
              <h3>Best Category</h3>
              <p className="card-value small">{isPrivateCategory(report.highestRatedCategory) ? 'Private' : (report.highestRatedCategory || 'N/A')}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon">📈</div>
            <div className="card-content">
              <h3>Trend</h3>
              <p className={`card-value trend ${!hasPrivateEntries ? report.trend?.toLowerCase() : ''}`}>
                {hasPrivateEntries ? 'Private' : (report.trend || 'Stable')}
              </p>
            </div>
          </div>
        </div>

        <div className="charts-grid">
          {renderCategoryChart(report)}
          {renderCategoryDistribution(report)}
        </div>

        <div className="detailed-breakdown">
          <h3>📋 Category Details</h3>
          <div className="category-list">
            {report.categoryAverages && Object.entries(report.categoryAverages).map(([category, average]) => (
              <div key={category} className={`category-item ${isPrivateCategory(category) ? 'private-category-item' : ''}`}>
                <div className="category-info">
                  <span className="category-name">{isPrivateCategory(category) ? '🔒 Private' : category}</span>
                  <span className="category-count">{isPrivateCategory(category) ? 'Private' : `${report.categoryBreakdown?.[category] || 0} entries`}</span>
                </div>
                <div className="category-rating">
                  {isPrivateCategory(category) ? (
                    <span className="private-rating-label">Private</span>
                  ) : (
                    <>
                      <div className="rating-bar">
                        <div className="rating-fill" style={{ width: `${(average / 10) * 100}%`, backgroundColor: average >= 8 ? '#4caf50' : average >= 5 ? '#ffd93d' : '#ff6b6b' }} />
                      </div>
                      <span className="rating-value">{average.toFixed(1)}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="period-info">
          <p>📅 Period: {report.startDate} to {report.endDate}</p>
        </div>
      </div>
    );
  };

  const renderCombinedReport = (data) => {
    if (!data) return null;
    return (
      <div className="combined-report">
        <div className="couple-average-card">
          <div className="couple-icon">💑</div>
          <h2>Couple Average Rating</h2>
          <p className="couple-average">{data.coupleAverageRating?.toFixed(1) || 'N/A'}</p>
          <p className="couple-subtitle">Combined relationship score</p>
        </div>
        <div className="partners-reports">
          {data.report1 && <div className="partner-report">{renderIndividualReport(data.report1, `${data.report1.username}'s Report`)}</div>}
          {data.report2 && <div className="partner-report">{renderIndividualReport(data.report2, `${data.report2.username}'s Report`)}</div>}
        </div>
      </div>
    );
  };

  if (loadingProfile) {
    return <div className="reports-container"><div className="loading-spinner">Loading...</div></div>;
  }

  if (!profile || !profile.maleName || !profile.femaleName || !profile.maleAge || !profile.femaleAge) {
    return (
      <div className="reports-container">
        <div className="profile-warning">
          <div className="warning-icon">⚠️</div>
          <h3>Profile Incomplete</h3>
          <p>Please complete your profile with both partner names and ages before generating reports.</p>
          <button className="complete-profile-button" onClick={() => navigate('/profile')}>Go to Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>📊 Relationship Reports</h1>
        <p>Generate insights and track your progress</p>
      </div>

      <div className="report-controls">
        <div className="control-group">
          <label htmlFor="reportType">Report Type</label>
          <select id="reportType" value={reportType} onChange={(e) => { setReportType(e.target.value); setFeedback(''); }} disabled={loading}>
            <option value="weekly">Weekly (Last 7 Days)</option>
            <option value="monthly">Monthly (Last 30 Days)</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="selectedPerson">Select Person/Couple</label>
          <select id="selectedPerson" value={selectedPerson} onChange={(e) => { setSelectedPerson(e.target.value); setFeedback(''); }} disabled={loading}>
            {getPersonOptions()}
          </select>
        </div>
        <button className="generate-button" onClick={handleGenerateReport} disabled={loading || !selectedPerson}>
          {loading ? 'Generating...' : '📊 Generate Report'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
          {(error.includes('Premium') || error.includes('Upgrade')) && (
            <button className="upgrade-prompt-btn" onClick={() => navigate('/plus')}>
              ⭐ Upgrade to Premium
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Generating your report...</p>
        </div>
      )}

      {reportData && !loading && renderAIFeedback()}

      {reportData && !loading && (
        <div className="report-display">
          {selectedPerson === 'couple' ? renderCombinedReport(reportData) : renderIndividualReport(reportData, `${selectedPerson}'s ${reportType} Report`)}
        </div>
      )}

      {!reportData && !loading && !error && (
        <div className="empty-state">
          <div className="empty-icon">📈</div>
          <h3>No Report Generated Yet</h3>
          <p>Select a report type and person, then click "Generate Report"</p>
        </div>
      )}
    </div>
  );
}

export default Reports;